import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { range, sortBy } from 'lodash';

import { useTableApi, useTableUniverse } from '~Components/TableUniverse';
import {
  useButton,
  useCurrency,
  useDeviceInfo,
  useDispatch,
  useLicenses,
  useLogger,
  useSelector,
  useStaffCan,
} from '~Hooks';
import PaymentStatus from '~Components/PaymentStatus';
import SplitModal from '~Scenes/Handy/Components/SplitModal';
import ActionButtons from './ActionButtons';

import { useTableContext } from '~Scenes/Handy/Context/TableContext';
import { BaseButton, COLORS } from '~Scenes/Handy/styles';
import NumberInput from '~ComponentsImproved/Numpad';
import { useStaffContext } from '~Scenes/Handy/Context/StaffProvider';
import { useChequeContext } from '~Scenes/Handy/Context/ChequeContext';
import { DISCOUNT_TYPE } from '../../../../../__queries__';
import itemsType from '~Scenes/Tables/Components/dragItemsType';
import ArticleItem from '~ComponentsImproved/TSR/ArticleItem';
import DragPreview from '~ComponentsImproved/TSR/DragPreview';
import PriceSection from './PriceSection';
import Logs from './Logs';
import { Box } from '~Styles';
import { useStorage } from '~Components/Storage';
import Icon from '~ComponentsImproved/Icon';
import compressIcon from '~Assets/icons/compress.svg';
import expandIcon from '~Assets/icons/expand.svg';
import { combineArticles } from '~Utils/combineArticles';
import { selectItem } from '~Utils/itemSelection';
import { Item } from '@baemingo/table-events/dist/types';
import { sortByCourseTypeOrder } from '~Utils/sortArticles';
import { useApolloClient } from '@apollo/client';
import useModal from '~ComponentsImproved/Modal/useModal';
import DemoNoPaymentsAllowed from '~Components/DemoHandler/NoPaymentsAllowed';
import useI18n from '~Translations';
import ServiceCharge from '~Scenes/Tables/Components/ServiceCharge';
import OfferItemWrapper from '~Scenes/POS/Components/Cart/Components/OfferItemWrapper';
import { groupOfferMatches } from '~Utils/offers';
import { useBookings } from '~Providers/BookingsProvider';
import useItemModifications from '~Hooks/ItemModifications/useItemModifications';
import { shouldShowModificationPopup } from '~Hooks/ItemModifications/utils';
import { ITEM_FRAGMENT } from '~Scenes/POS/Queries/query';
import { CompleteBongQuery_venue_company_categories_items as MenuItem } from '~Scenes/POS/Queries/__queries__';
import HandyItemModifications from '~Scenes/Handy/Scenes/Ordering/ItemModifications';
import useChequeItemQuantity, { AddedItemEvent } from '~Scenes/Tables/Hooks/useChequeItemQuantity';
import QuantityStepper from '~ComponentsImproved/TSR/QuantityStepper';

const Wrapper = styled.div<{
  isPhone: boolean;
}>`
  flex: 0 0 calc(100vw - 80px);
  background: ${COLORS.blueGray1300};
  display: flex;
  padding-bottom: ${({ isPhone }) => (isPhone ? '24px' : '')};
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
  box-shadow: 0px 0px 64px 0px rgba(0, 0, 0, 0.32);
`;

const Cart = styled.div`
  flex: 1;
  color: #fff;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const Items = styled.div<{ blurCart: boolean }>`
  ${({ blurCart }) => blurCart && 'opacity: 0.6'};
  flex: 1;
  position: relative;
  overflow-y: scroll;
  // hide scrollbar
  -ms-overflow-style: none;
  scrollbar-width: none;
  font-weight: 600;
  font-size: 14px;
  padding-bottom: 24px;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const SmallButtons = styled.div`
  z-index: 1;
  position: absolute;
  top: 0;
  transform: translateY(-100%);
  right: 0;
  padding: 0 8px 8px 0;
  display: flex;
  gap: 4px;
  z-index: 2;
`;

export const SmallIconButton = styled(BaseButton)`
  border: 1px solid var(--colorBlueGray300);
  background-color: var(--colorBlueGray1300);
  border-radius: 4px;
  height: 32px;
  width: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
`;

type PaymentInfo = {
  amount: number;
  id: string;
};

export type DiscountObj = {
  rate: number;
  type: DISCOUNT_TYPE;
  itemIds: string[];
};

const ChequeDetails = () => {
  const logger = useLogger('handy');
  const tableAPI = useTableApi();
  const universe = useTableUniverse();
  const currency = useCurrency();
  const staff = useSelector((state) => state.staff);
  const { selectedTable } = useTableContext();
  const { selectedCheque, setSelectedCheque } = useChequeContext();
  const { authedStaff } = useStaffContext();
  const licenses = useLicenses();
  const deviceInfo = useDeviceInfo();
  const [storage, setStorage] = useStorage();
  const client = useApolloClient();
  const demoMode = useSelector((state) => state.demoMode);
  const demoPopup = useModal();
  const { i18n } = useI18n();
  const dispatch = useDispatch();
  const staffCan = useStaffCan();
  const bookings = useBookings();

  const [isSplittingCheque, setIsSplittingCheque] = useState<boolean>(false);
  // blurCart is used to pull focus towards prompts
  const [blurCart, setBlurCart] = useState<boolean>(false);
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [itemDrag, setItemDrag] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  useEffect(() => {
    if (isDragging) {
      setItemDrag(true);
    } else if (isDragging == false && itemDrag == true) {
      setSelectedItemIds([]);
      setItemDrag(false);
    }
  }, [isDragging]);

  const order = useSelector(
    (state) => state.POSOrders.filter((o) => o.chequeId === selectedCheque?.id)[0] || null,
  );
  const payment = useSelector(
    (state) => state.POSPayments.filter((p) => order && p.order === order.id)[0] || null,
  );

  const chequesOnTable = universe.cheques.filter((c) => c.tableId === selectedTable?.id);
  const cheque = universe.cheques.find((c) => c.id === selectedCheque?.id);
  const chequeItems = universe.items.filter((c) => c.chequeId === selectedCheque?.id);
  const nonSelectedItems = chequeItems.filter((item) => !selectedItemIds.includes(item.id));
  const nonSelectedItemsTotalSum = nonSelectedItems.reduce((acc, item) => {
    return (acc += item.amount - item.discount);
  }, 0);
  const staffCanMutate = staffCan.mutateCheque(selectedCheque?.id ?? '');
  const items = universe.items.filter((i) => i.chequeId === selectedCheque?.id);
  const amountLeftToPay = cheque ? cheque.amount - cheque.discount - cheque.paidAmount : 0;

  // Holds info such as payment method and amount, as we are setting up the payment
  const [contextualPaymentInfo, setContextualPaymentInfo] = useState<PaymentInfo | null>(null);

  // A fresh object every time an item is added, so the same product id can
  // retrigger the animation even if it was already pulsing/flashing
  const [addedItemEvent, setAddedItemEvent] = useState<AddedItemEvent | null>(null);

  // Reset the selected items when we change the active cheque. Also clear any
  // pending add-animation - otherwise it survives on this component (which
  // never unmounts, see the `!selectedCheque` early return below) and replays
  // on ArticleItem's next mount, e.g. when navigating back into this cheque.
  useEffect(() => {
    setSelectedItemIds([]);
    setAddedItemEvent(null);
  }, [selectedCheque]);

  const hasOrderLicense = licenses.hasHandheldPro || licenses.hasHandheldOrder;
  const venueId = useSelector((state) => state.venueId);

  const compressButton = useButton({
    onClick: () => setStorage({ ...storage, compressedCart: !storage.compressedCart }),
  });

  const [duplicateModalItem, setDuplicateModalItem] = useState<{
    sourceItem: Item;
    menuItem: MenuItem;
    count: number;
  } | null>(null);

  // Look up the menu item of the selection so we know its modification groups
  const selectedSourceItem = items.find((item) => selectedItemIds.includes(item.id)) ?? null;

  // Look up the menu item so we know its modification groups
  const selectedMenuItem = useMemo<MenuItem | null>(() => {
    if (!selectedSourceItem) {
      return null;
    }

    try {
      const menuItem = client.readFragment<MenuItem>({
        id: `Item${selectedSourceItem.itemId}`,
        fragment: ITEM_FRAGMENT,
      });

      if (!menuItem) {
        return null;
      }

      // This fragment only selects `upsales { id }`, not the full
      // `hideInPOS`/`items` fields HandyItemModifications needs to render
      // upsells - since duplicating never offers upsells, strip them here
      // rather than let the popup crash trying to read them.
      return { ...menuItem, upsales: [] };
    } catch (e) {
      return null;
    }
  }, [selectedSourceItem?.itemId]);

  const itemModifications = useItemModifications(selectedMenuItem?.modificationGroups ?? []);

  const deviceWakeState = useSelector((state) => state.deviceWakeState);
  const disallowRemove = Boolean(
    cheque?.paidAmount && nonSelectedItemsTotalSum <= cheque.paidAmount,
  );

  const quantity = useChequeItemQuantity({
    chequeId: selectedCheque?.id,
    tableId: selectedTable?.id ?? '',
    tableName: selectedTable?.code ?? '',
    items,
    selectedItemIds,
    setSelectedItemIds,
    setAddedItemEvent,
    compressedCart: storage.compressedCart,
    // Same rule as opening the order screen (ChequePanel/Footer): don't allow
    // adding more items once a payment has been made on the cheque
    canAdd: staffCanMutate && !cheque?.paidAmount,
    canRemove:
      staffCanMutate && staffCan.REFUND && !disallowRemove && !deviceWakeState.syncingTableEvents,
    // Let staff pick variations (pre-selected to match the item being copied)
    // instead of blindly copying them, either when the product would normally
    // show the popup on add, or when the item being copied was configured with
    // any modifications - added variations OR removed default ingredients
    requiresModifications: (sourceItem) =>
      Boolean(selectedMenuItem) &&
      (shouldShowModificationPopup(itemModifications.modificationGroups) ||
        sourceItem.richVariations.length > 0 ||
        sourceItem.removedVariations.length > 0),
    onRequestModifications: (sourceItem, count) =>
      setDuplicateModalItem({ sourceItem, menuItem: selectedMenuItem!, count }),
  });

  // Calculate sorted and combined items (must be before early return for hooks)
  const sortedItems = sortByCourseTypeOrder(items, client);

  // Group items by offers to show orange grouped headers
  // Uses Tables-specific utility that works directly with TableItem (no conversion needed)
  const grouped = useMemo(() => {
    if (!venueId) {
      return { matches: [], unmatched: sortedItems };
    }
    return groupOfferMatches(sortedItems, venueId, client, i18n);
  }, [sortedItems, venueId, client, i18n]);

  if (!cheque || !selectedCheque) {
    return <Wrapper isPhone={false} />;
  }

  const initiatePayment = (payment: PaymentInfo) => {
    setContextualPaymentInfo(null);
    // Sometimes the amount can contain floating point errors, so we round it
    // to two decimal points. This will never cause a rounding error as we only
    // deal with whole cents.
    // Example: 100.23000000000001 => 100.23
    const roundedAmount = Math.round(payment.amount * 100) / 100;

    const orderId = selectedTable?.code ?? '---';

    tableAPI.initiatePayment(
      selectedCheque.id,
      orderId,
      payment.id,
      roundedAmount,
      undefined,
      staff ? staff.id : undefined,
      undefined,
      storage.serviceType,
      selectedCheque.bookingData?.externalId,
    );

    logger.info('Triggered payment', {
      cheque,
      table: selectedTable,
      amount: payment.amount,
      roundedAmount,
      currency,
      paymentMethod: payment.id,
      bookingId: cheque.bookingData?.externalId,
      amountLeftToPay,
      isPartial: payment.amount !== amountLeftToPay,
    });
  };

  // Un-selects all items
  const handleCancelSelection = () => {
    setSelectedItemIds([]);
  };

  const handleRemoveCheque = () => {
    // If the cheque has items, it cannot be removed
    if (items.length > 0) {
      return;
    }
    logger.info('Staff deleted cheque', {
      cheque,
      table: selectedTable,
      staff: authedStaff,
    });

    tableAPI.removeCheque(selectedCheque.id, selectedTable?.id || '');

    const filteredCheques = chequesOnTable.filter((c) => c.id !== selectedCheque.id);

    const newSelectedCheque = filteredCheques[0] ?? null;
    setSelectedCheque(newSelectedCheque);
    // NOTE: If there are no cheques on the table after delete, the
    // effect in TableSelector will boot the user back
    setSelectedItemIds([]);
  };

  const handleSplitCheque = (count: number) => {
    logger.info('Staff split cheque', {
      cheque: cheque,
      intoNr: count,
      table: selectedTable,
      staff: authedStaff,
    });

    const ids = tableAPI.splitCheque(selectedCheque.id, count);
    tableAPI.log.splitCheque(selectedCheque.id, ids);
    setIsSplittingCheque(false);
  };

  function handleItemSelect(item: Item, isInOfferGroup?: boolean) {
    if (!hasOrderLicense || !staffCanMutate) {
      return;
    }

    const itemsToSelect = selectItem({
      selectedItem: item,
      items: items,
      selectedItems: selectedItemIds,
      compressedCart: isInOfferGroup ? false : storage.compressedCart,
    });

    setSelectedItemIds(itemsToSelect);
  }

  /**
   * Renders a single article item with proper selection and drag handling
   */
  const renderArticleItem = (item: Item, preview?: boolean, isInOfferGroup?: boolean) => {
    const selected = selectedItemIds.includes(item.id);
    if (preview && !selected) {
      return null;
    }
    return (
      <ArticleItem
        key={item.id}
        item={item}
        onClick={() => handleItemSelect(item, isInOfferGroup)}
        preview={preview}
        selected={selected}
        selectedItems={selectedItemIds}
        setIsDragging={setIsDragging}
        hidePrice={isInOfferGroup}
        disallowDrag={cheque ? cheque.paidAmount > 0 : false}
        addedItemEvent={addedItemEvent}
      />
    );
  };

  /**
   * Renders items grouped by offers (with orange headers) and ungrouped items
   */
  const renderItems = (preview?: boolean) => {
    const itemsArr = storage.compressedCart ? combineArticles(sortedItems) : sortedItems;

    return (
      <Items blurCart={blurCart}>
        {preview ? (
          // In preview mode (drag), show all selected items without grouping
          itemsArr.map((item) => renderArticleItem(item, preview))
        ) : (
          <>
            {/* Render unmatched items (not part of any offer) */}
            {grouped.unmatched.map((unmatchedItem) => {
              // Find the original Item by id, needed because of combineArticles
              const tableItem = itemsArr.find((i) => i.id === unmatchedItem.id);
              if (!tableItem) return null;
              return renderArticleItem(tableItem, preview, false);
            })}

            {/* Render matched offer groups with orange headers */}
            {grouped.matches.map((offerMatch, index) => {
              // Calculate total old and new prices for this offer group
              const offerPrice = offerMatch.items.reduce(
                (acc, item) => {
                  acc.oldPrice += item.amount;
                  acc.newPrice += item.amount - (item.discount ?? 0);
                  return acc;
                },
                {
                  oldPrice: 0,
                  newPrice: 0,
                },
              );

              return (
                <OfferItemWrapper
                  key={`offer-${index}`}
                  offerId={offerMatch.offerId}
                  oldPrice={offerPrice.oldPrice}
                  newPrice={offerPrice.newPrice}>
                  {offerMatch.items.map((matchedItem) => {
                    // Hide individual item prices since offer header shows total
                    return renderArticleItem(matchedItem, preview, true);
                  })}
                </OfferItemWrapper>
              );
            })}
          </>
        )}
        {cheque.serviceCharge && (
          <ServiceCharge chequeId={cheque.id} percentage={cheque.serviceCharge.percentage} />
        )}
      </Items>
    );
  };

  return (
    <>
      {/* Portal is needed because of the full page CSS translate. Targets the
          anchor rendered next to <CartProvider isTSR> in TableSelector rather
          than document.body, since that anchor sits outside the translate. */}
      {duplicateModalItem &&
        createPortal(
          <HandyItemModifications
            item={duplicateModalItem.menuItem}
            onCancel={() => setDuplicateModalItem(null)}
            prevSelected={{
              variations: duplicateModalItem.sourceItem.richVariations,
              removedVariations: duplicateModalItem.sourceItem.removedVariations,
            }}
            onComplete={(variations, removedVariations) => {
              quantity.duplicate(
                duplicateModalItem.sourceItem,
                duplicateModalItem.count,
                variations,
                removedVariations,
              );
              setDuplicateModalItem(null);
            }}
          />,
          document.getElementById('tsr-portal-anchor') ?? document.body,
        )}

      <Wrapper isPhone={Boolean(deviceInfo?.isPhone)}>
        <Cart>
          {renderItems()}
          <Box style={{ position: 'relative' }}>
            <SmallButtons>
              {hasOrderLicense && <Logs />}
              <SmallIconButton {...compressButton}>
                <Icon
                  src={storage.compressedCart ? expandIcon : compressIcon}
                  color={'var(--colorBlueGray100)'}
                />
              </SmallIconButton>
            </SmallButtons>
            <PriceSection />
          </Box>
        </Cart>
        {payment && <PaymentStatus paymentId={payment.id} status={payment.status} />}
        {!payment && (
          <ActionButtons
            onSplit={() => setIsSplittingCheque(true)}
            disableSplit={cheque.paidAmount > 0}
            onCancelSelection={handleCancelSelection}
            onDeleteCheque={handleRemoveCheque}
            canDeleteCheque={items.length === 0}
            selectedItemIds={selectedItemIds}
            disallowRemove={disallowRemove}
            pendingRemoveIds={quantity.pendingRemoveIds}
            onClearPendingRemove={quantity.clearPendingRemove}
            quantityStepper={
              quantity.canAdjust && staffCanMutate ? (
                <QuantityStepper
                  pending={quantity.pending}
                  canIncrement={quantity.canIncrement}
                  canDecrement={quantity.canDecrement}
                  onIncrement={quantity.increment}
                  onDecrement={quantity.decrement}
                  onConfirm={quantity.confirm}
                  incrementDisabledToast={i18n('TOAST.DisableOrderingAfterPayment')}
                  decrementDisabledToast={
                    deviceWakeState.syncingTableEvents
                      ? i18n('Handy.Tables.LoadingEvents')
                      : i18n('TM.CantRemoveItemsWithPayments')
                  }
                />
              ) : null
            }
          />
        )}

        {isSplittingCheque && (
          <SplitModal
            range={range(2, 10)}
            onSelect={handleSplitCheque}
            onCancel={() => setIsSplittingCheque(false)}
          />
        )}

        {contextualPaymentInfo && !contextualPaymentInfo.amount && (
          <NumberInput
            decimal
            initialValueIsPlaceholder
            initialValue={(amountLeftToPay / 100).toString()}
            maxValue={amountLeftToPay / 100}
            onSubmit={(amount) => {
              setContextualPaymentInfo((prev) => ({
                id: prev!.id,
                amount: Math.round(parseFloat(amount) * 100),
              }));
              initiatePayment({
                id: contextualPaymentInfo!.id,
                amount: Math.round(parseFloat(amount) * 100),
              });
            }}
            onClose={() => setContextualPaymentInfo(null)}
          />
        )}
      </Wrapper>

      {/* Portal is needed because of the full page CSS translate */}
      {createPortal(
        <DragPreview type={itemsType.ITEM} renderItem={renderItems(true)} />,
        document.body,
      )}

      <DemoNoPaymentsAllowed isShowing={demoPopup.isShowing} toggle={demoPopup.toggle} />
    </>
  );
};

export default React.memo(ChequeDetails);
